import { type Observable, of, shareReplay } from 'rxjs';
import type { HttpDataPaginationResponse, HttpDataResponse } from '@common/types/http-response.type';
import type { SitePage, SitePageCopyRequest, SitePageUpdateRequest } from '@common/types/site-page.type';

import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';

import { BaseHttpService } from '@common/services/REST/base.http.service';

@Injectable({
    providedIn: 'root',
})
export class PagesHttpService extends BaseHttpService {
    public getSitePage(siteId: number | string, pageId: number | 'product'): Observable<SitePage> {
        return this.http.get<HttpDataResponse<SitePage>>(this.formatApiUrl(`sites/${siteId}/pages/${pageId}`)).pipe(
            shareReplay(),
            map((res) => res.data),
            catchError(() => {
                console.error(`Failed to fetch page with id ${pageId} for site with id ${siteId}`);
                return of(null);
            })
        );
    }

    /**
     * The page without its content — what the open page polls for its version. Errors are left to the
     * caller, which needs to tell a deleted page from a dropped connection.
     */
    public getSitePageInfo(
        siteId: number | string,
        pageId: number | string
    ): Observable<Pick<SitePage, 'id' | 'version'>> {
        // Served bare, not inside `data` like the rest of this API.
        return this.http
            .get<
                Pick<SitePage, 'id' | 'version'> | HttpDataResponse<Pick<SitePage, 'id' | 'version'>>
            >(this.formatApiUrl(`sites/${siteId}/pages/${pageId}/info`))
            .pipe(map((res) => ('data' in res ? res.data : res)));
    }

    public getSitePages(
        siteId: number | string,
        params?: { page: number; perPage: number }
    ): Observable<{ pages: SitePage[]; total: number }> {
        return this.http
            .get<HttpDataPaginationResponse<SitePage>>(this.formatApiUrl(`sites/${siteId}/pages`), {
                params: {
                    page: params?.page || 1,
                    perPage: params?.perPage || 100,
                },
            })
            .pipe(
                shareReplay(),
                map((res) => {
                    return {
                        pages: res.data,
                        total: res.meta.total,
                    };
                }),
                catchError(() => {
                    console.error(`Failed to fetch pages for site with id ${siteId}`);
                    return of(null);
                })
            );
    }

    public activatePage(siteId: number | string, pageId: number | string): Observable<SitePage> {
        return this.http
            .put<HttpDataResponse<SitePage>>(this.formatApiUrl(`sites/${siteId}/pages/${pageId}/activate`), {})
            .pipe(
                shareReplay(),
                map((res) => res.data)
            );
    }

    public disablePage(siteId: number | string, pageId: number | string): Observable<SitePage> {
        return this.http
            .put<HttpDataResponse<SitePage>>(this.formatApiUrl(`sites/${siteId}/pages/${pageId}/disable`), {})
            .pipe(
                shareReplay(),
                map((res) => res.data)
            );
    }

    public deleteSitePage(siteId: number | string, pageId: number | string): Observable<void> {
        return this.http.delete<void>(this.formatApiUrl(`sites/${siteId}/pages/${pageId}`));
    }

    public copyPage(
        siteId: number | string,
        pageId: number | string,
        payload: SitePageCopyRequest
    ): Observable<SitePage> {
        return this.http
            .post<HttpDataResponse<SitePage>>(this.formatApiUrl(`sites/${siteId}/pages/${pageId}/copy`), payload)
            .pipe(
                shareReplay(),
                map((res) => res.data)
            );
    }

    public createPage(siteId: number | string, payload: SitePageUpdateRequest): Observable<SitePage> {
        return this.http.post<HttpDataResponse<SitePage>>(this.formatApiUrl(`sites/${siteId}/pages`), payload).pipe(
            shareReplay(),
            map((res) => res.data)
        );
    }

    public updatePage(
        siteId: number | string,
        pageId: number | string,
        payload: SitePageUpdateRequest
    ): Observable<SitePage> {
        return this.http
            .post<HttpDataResponse<SitePage>>(this.formatApiUrl(`sites/${siteId}/pages/${pageId}`), payload)
            .pipe(
                shareReplay(),
                map((res) => res.data)
            );
    }

    public resetSiteToPublished(siteId: number | string, pageId: number | string) {
        return this.http
            .post<HttpDataResponse<SitePage>>(this.formatApiUrl(`/sites/${siteId}/pages/${pageId}/reset`), {})
            .pipe(
                shareReplay(),
                map((res) => res.data)
            );
    }
}
